import * as Constants from "expo-constants";
import * as Device from "expo-device";
import type * as ExpoNotifications from "expo-notifications";
import { useRouter, useSegments } from "expo-router";
import { useEffect, useRef } from "react";
import { Alert, AppState, Linking, Platform } from "react-native";

import { registerPushToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { shouldPushDeepLink, type LastPush } from "@/lib/deep-link-nav-guard";
import { useNotification } from "@/lib/notifications";
import { safePushDeepLink } from "@/lib/push-deep-link";
import { isRetryablePushError } from "@/lib/push-error-classifier";
import { describePushError, getPushTokenWithRetry } from "@/lib/push-token-retry";
import { addSentryBreadcrumb, captureSentryEvent } from "@/lib/sentry-init";

// Expo Go (SDK 53+) removed remote push support, so importing expo-notifications
// throws at module load. Lazy-require it outside Expo Go; in Expo Go the whole
// provider no-ops. Real push still needs a dev build or standalone.
const isExpoGo = Constants.default.executionEnvironment === "storeClient";
/* eslint-disable @typescript-eslint/no-require-imports */
const Notifications: typeof ExpoNotifications | null = isExpoGo
    ? null
    : require("expo-notifications");
/* eslint-enable @typescript-eslint/no-require-imports */

// Suppress OS banner in foreground — WS already delivers in-app toasts.
// Push notifications received while foregrounded fire addNotificationReceivedListener instead.
if (Notifications) {
    Notifications.setNotificationHandler({
        handleNotification: async () => ({
            shouldShowBanner: false,
            shouldPlaySound: true,
            shouldSetBadge: true,
            shouldShowList: true
        })
    });
}

// Module-level flags — reset only on full JS reload. Prevents the cold-start
// tap response (sticky in iOS) from being replayed on every re-render or
// auth state change, which would otherwise loop router.push → crash → reload.
let coldStartHandled = false;
const handledResponseIds = new Set<string>();
const lastDeepLinkPush: LastPush = { href: "", at: 0 };

/**
 * A notification's deep link, if it is one of the app's real screens (see
 * lib/push-deep-link.ts); undefined otherwise, so the tap just opens the app.
 */
function allowedDeepLink(data: Record<string, unknown> | undefined): string | undefined {
    const raw = data?.deepLink;
    if (raw === undefined || raw === null) {
        return undefined;
    }
    const safe = safePushDeepLink(raw);
    if (!safe) {
        console.warn(
            "[push] ignored a deep link that is not an app screen:",
            String(raw).slice(0, 80)
        );
        return undefined;
    }
    return safe;
}

// Retry wrapper around getExpoPushTokenAsync — see push-token-retry.ts for
// why (transient Expo-service failures, TASKHEALTH-MOBILE-2C/2E) and why the
// helpers live in their own import-free module (unit-testability).
function getExpoPushTokenWithRetry(
    N: typeof ExpoNotifications,
    projectId: string
): Promise<string> {
    return getPushTokenWithRetry(
        async () => (await N.getExpoPushTokenAsync({ projectId })).data,
        (attempt, err) =>
            addSentryBreadcrumb({
                category: "push-register",
                level: "warning",
                message: `[push-register] token_fetch_retry ${JSON.stringify({
                    attempt,
                    ...describePushError(err)
                })}`
            })
    );
}

export function PushNotificationProvider({ children }: { children: React.ReactNode }) {
    const router = useRouter();
    const segments = useSegments();
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const lastNativeTokenRef = useRef<string | null>(null);
    const segmentsRef = useRef(segments);
    segmentsRef.current = segments;
    const rnIdRef = useRef<number | null>(user?.rnId ?? null);
    rnIdRef.current = user?.rnId ?? null;
    const routerRef = useRef(router);
    routerRef.current = router;
    const showNotificationRef = useRef(showNotification);
    showNotificationRef.current = showNotification;

    // Navigate from a tap, but only once per unique notification response —
    // guards against the tap listener AND getLastNotificationResponseAsync
    // both firing for the same tap on iOS.
    const navigateFromTap = (responseId: string, deepLink: string | undefined) => {
        if (!deepLink || handledResponseIds.has(responseId)) {
            return;
        }
        handledResponseIds.add(responseId);
        // Defer past the current frame: at cold start this fires while the root
        // stack's initial replace("/(tabs)") native transaction is still
        // settling, and pushing into that transaction makes react-native-screens
        // enqueue the same RNSScreen twice → fatal NSInvalidArgumentException
        // relaunch loop (TASKHEALTH-MOBILE-1F, breadcrumbs show the crash <500ms
        // after boot as (tabs) mounts). 350ms is imperceptible after a tap and
        // lets the initial mount commit first. The href guard dedupes two
        // DIFFERENT responses resolving to the same destination back-to-back —
        // the response-id set above only catches the same response twice.
        setTimeout(() => {
            if (!shouldPushDeepLink(lastDeepLinkPush, deepLink, Date.now())) {
                return;
            }
            routerRef.current.push(deepLink as any);
        }, 350);
    };

    // Mount-only: install listeners once. Keeping deps = [] means taps arriving
    // during auth transitions aren't lost to a listener re-subscribe race.
    useEffect(() => {
        console.warn(
            "[push] provider mount — platform:",
            Platform.OS,
            "isDevice:",
            Device.isDevice,
            "brand:",
            Device.brand,
            "modelName:",
            Device.modelName,
            "isExpoGo:",
            isExpoGo,
            "notificationsAvailable:",
            !!Notifications
        );
        if (Platform.OS === "web") {
            console.warn("[push] skipping — web platform");
            return;
        }
        if (!Notifications) {
            console.warn("[push] skipping — expo-notifications not loaded (Expo Go?)");
            return;
        }
        // iOS simulator cannot get APNs tokens. Android emulators CAN get FCM tokens
        // if the image has Google Play Services, so we let Android through.
        if (!Device.isDevice && Platform.OS === "ios") {
            console.warn("[push] skipping — iOS simulator cannot receive APNs tokens");
            return;
        }
        if (!Device.isDevice && Platform.OS === "android") {
            console.warn(
                "[push] Android emulator — attempting registration. Requires a Google Play system image; AOSP images will fail at getExpoPushTokenAsync."
            );
        }
        const N = Notifications;

        void N.setBadgeCountAsync(0);
        const appStateSub = AppState.addEventListener("change", (state) => {
            if (state === "active") {
                void N.setBadgeCountAsync(0);
            }
        });

        // Foreground: show in-app toast instead of OS banner.
        // Suppress on chat screens to avoid noise.
        const foregroundSub = N.addNotificationReceivedListener((notification) => {
            const data = notification.request.content.data as Record<string, unknown>;
            const isChatNotification = data?.type === "ticket_message";
            const onChatScreen =
                segmentsRef.current.includes("support" as never) ||
                segmentsRef.current.includes("tickets" as never);

            if (isChatNotification && onChatScreen) {
                return;
            }

            const { title, body } = notification.request.content;
            const deepLink = allowedDeepLink(data);
            const message = [title, body].filter(Boolean).join(" — ") || "New notification";

            showNotificationRef.current(
                message,
                "info",
                deepLink
                    ? () => {
                          routerRef.current.push(deepLink as any);
                      }
                    : undefined
            );
        });

        // Native token refresh → fetch the fresh Expo-wrapped token and register it.
        // See rnIdRef — the listener is mounted once but needs to see the current
        // rnId at the time the token actually rotates.
        const tokenSub = N.addPushTokenListener((tokenEvent) => {
            const nativeToken = tokenEvent.data;
            if (lastNativeTokenRef.current === nativeToken) {
                return;
            }
            lastNativeTokenRef.current = nativeToken;
            void (async () => {
                const currentRnId = rnIdRef.current;
                if (!currentRnId) {
                    console.warn("[push-token] Native token rotated but no rnId — skipping");
                    return;
                }
                const projectId = Constants.default.expoConfig?.extra?.eas?.projectId as
                    string | undefined;
                if (!projectId) {
                    console.warn("[push-token] Native token rotated but no projectId — skipping");
                    return;
                }
                try {
                    const expoToken = await getExpoPushTokenWithRetry(N, projectId);
                    const result = await registerPushToken(
                        currentRnId,
                        expoToken,
                        Platform.OS as "ios" | "android"
                    );
                    console.warn(
                        "[push-token] Refreshed and sent to server:",
                        expoToken,
                        "rnId:",
                        currentRnId,
                        "result:",
                        result
                    );
                } catch (err) {
                    console.warn("[push-token] Refresh failed:", err);
                    const isOffline = isRetryablePushError(err);
                    captureSentryEvent({
                        message: "[push-register] token_refresh_failed",
                        level: isOffline ? "warning" : "error",
                        tags: { push_step: "token_refresh_failed", push_platform: Platform.OS },
                        extra: {
                            rnId: currentRnId,
                            platform: Platform.OS,
                            ...describePushError(err)
                        }
                    });
                }
            })();
        });

        // Tap handler (app foregrounded or backgrounded, not killed).
        const tapSub = N.addNotificationResponseReceivedListener((response) => {
            const data = response.notification.request.content.data as Record<string, unknown>;
            const deepLink = allowedDeepLink(data);
            // Same ID as the cold-start path uses — dedupes if iOS delivers both.
            navigateFromTap(response.notification.request.identifier, deepLink);
            coldStartHandled = true;
        });

        return () => {
            foregroundSub.remove();
            tokenSub.remove();
            tapSub.remove();
            appStateSub.remove();
        };
    }, []);

    // Re-register permission / push token whenever the logged-in rnId changes.
    useEffect(() => {
        if (Platform.OS === "web" || !Notifications) {
            return;
        }
        if (!Device.isDevice && Platform.OS === "ios") {
            console.warn("[push] skipping token registration — iOS simulator");
            return;
        }
        void registerForPushNotifications(user?.rnId ?? null);
    }, [user?.rnId]);

    // Cold-start tap: the OS remembers the last-tapped notification across JS
    // reloads, so without clearing it the same response re-fires every reload
    // and loops the app. Wait for auth (so we don't race RootNavigator's
    // unauthenticated redirect to /welcome), handle once, then clear the
    // OS-side record so a dev-client reload can't replay it.
    useEffect(() => {
        if (Platform.OS === "web" || !Device.isDevice || !Notifications) {
            return;
        }
        if (coldStartHandled || !user?.rnId) {
            return;
        }
        coldStartHandled = true;
        const N = Notifications;
        const response = N.getLastNotificationResponse();
        if (!response) {
            return;
        }
        const data = response.notification.request.content.data as Record<string, unknown>;
        const deepLink = allowedDeepLink(data);
        navigateFromTap(response.notification.request.identifier, deepLink);
        N.clearLastNotificationResponse();
    }, [user?.rnId]);

    return children as React.ReactElement;
}

async function registerForPushNotifications(rnId: number | null): Promise<void> {
    // Diagnostics: the push-registration path is otherwise invisible server-side
    // (it only console.warn'd, which lives in LogRocket). These report to Sentry
    // so we can see, per real device, exactly which step the flow reaches and
    // where/why it fails on Android. Tagged by platform + step for filtering.
    const deviceCtx = (): Record<string, unknown> => ({
        rnId,
        platform: Platform.OS,
        isDevice: Device.isDevice,
        brand: Device.brand,
        modelName: Device.modelName,
        osVersion: Device.osVersion
    });
    const trail = (step: string, extra?: Record<string, unknown>) =>
        addSentryBreadcrumb({
            category: "push-register",
            level: "info",
            message: `[push-register] ${step} ${JSON.stringify({ ...deviceCtx(), ...extra })}`
        });
    const outcome = (
        step: string,
        level: "info" | "warning" | "error",
        extra?: Record<string, unknown>
    ) =>
        captureSentryEvent({
            message: `[push-register] ${step}`,
            level,
            tags: { push_step: step, push_platform: Platform.OS },
            extra: { ...deviceCtx(), ...extra }
        });

    if (!Notifications) {
        console.warn("[push] Skipping — expo-notifications unavailable (Expo Go?)");
        outcome("notifications_unavailable", "warning");
        return;
    }
    const N = Notifications;

    console.warn(
        "[push] registerForPushNotifications called, rnId:",
        rnId,
        "platform:",
        Platform.OS,
        "isDevice:",
        Device.isDevice
    );
    trail("called");

    // Set up Android notification channels
    if (Platform.OS === "android") {
        await Promise.all([
            N.setNotificationChannelAsync("messages", {
                name: "Chat Messages",
                importance: N.AndroidImportance.HIGH,
                vibrationPattern: [0, 250, 250, 250],
                lightColor: "#45D2B0"
            }),
            N.setNotificationChannelAsync("onboarding", {
                name: "Onboarding Updates",
                importance: N.AndroidImportance.HIGH,
                vibrationPattern: [0, 250, 250, 250],
                lightColor: "#45D2B0"
            }),
            N.setNotificationChannelAsync("visits", {
                name: "Visit Updates",
                importance: N.AndroidImportance.DEFAULT,
                vibrationPattern: [0, 250, 250, 250],
                lightColor: "#45D2B0"
            }),
            N.setNotificationChannelAsync("medications", {
                name: "Medication Updates",
                importance: N.AndroidImportance.DEFAULT,
                vibrationPattern: [0, 250, 250, 250],
                lightColor: "#45D2B0"
            })
        ]);
    }

    if (Platform.OS === "android") {
        trail("android_channels_set");
    }

    const { status: existingStatus } = await N.getPermissionsAsync();
    console.warn("[push] existing permission status:", existingStatus);
    trail("permission_existing", { existingStatus });
    let finalStatus = existingStatus;

    // iOS won't re-prompt once denied — route the user to system Settings instead.
    if (existingStatus === "denied") {
        outcome("permission_denied", "warning", { existingStatus });
        Alert.alert(
            "Enable notifications",
            "Turn on notifications to get alerts for new visits, messages, and important updates.",
            [
                { text: "Not now", style: "cancel" },
                {
                    text: "Open Settings",
                    onPress: () => {
                        void Linking.openSettings();
                    }
                }
            ]
        );
        return;
    }

    if (existingStatus !== "granted") {
        const { status } = await N.requestPermissionsAsync();
        console.warn("[push] requested permission, got:", status);
        trail("permission_requested", { requestedStatus: status });
        finalStatus = status;
    }

    if (finalStatus !== "granted") {
        console.warn("[push] permission not granted — aborting");
        outcome("permission_not_granted", "warning", { finalStatus });
        return;
    }

    const projectId = Constants.default.expoConfig?.extra?.eas?.projectId as string | undefined;
    console.warn("[push] projectId:", projectId);
    if (!projectId) {
        console.warn("[push-token] No projectId found in app config");
        outcome("no_project_id", "error");
        return;
    }

    try {
        // Bug 14 — skip the token fetch entirely when not authenticated.
        // The previous behavior fetched the token, logged "[push-token] Got
        // token: X rnId: null", then warned "skipping backend registration".
        // The useEffect that calls this also runs once with `rnId = null` (auth
        // not yet hydrated) AND once with `rnId = 22` (auth resolved), so the
        // token fetch happened twice and produced confusing duplicate
        // "Got token" log lines for the same physical token (LogRocket trace
        // for visit 205, native session). Deferring the fetch removes the
        // first-fire noise without changing behavior on the live path.
        if (!rnId) {
            return;
        }
        trail("fetching_expo_token", { projectId });
        const token = await getExpoPushTokenWithRetry(N, projectId);
        console.warn("[push-token] Got token:", token, "rnId:", rnId);
        trail("got_expo_token", { tokenPrefix: token.slice(0, 24) });
        const result = await registerPushToken(rnId, token, Platform.OS as "ios" | "android");
        console.warn("[push-token] Registered with backend:", result);
        outcome("token_registered", "info", { tokenPrefix: token.slice(0, 24) });
    } catch (err) {
        console.warn("[push-token] Failed to get/register push token:", err);
        const isOffline = isRetryablePushError(err);
        outcome(
            isOffline ? "token_or_register_offline" : "token_or_register_failed",
            isOffline ? "warning" : "error",
            describePushError(err)
        );
    }
}
